import { Box } from '@mui/material'
import { useContext, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EmployeeBox, GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import ProductAdUnitsTable from '../../components/ProductAdUnitsTable'
import { PrebidSettingsAlert } from './components/PrebidSettingsAlert'
import { usePrebidBehavioursRadioGroup } from './hooks/usePrebidBehavioursRadioGroup'
import { Schema } from './schema'
import { isUnfilledRecoveryConfigured } from './utils'

export const Form = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain')
  const { domain } = useContext(AddEditDomainContext)
  const { control, setValue, watch } = useFormContext<Schema>()

  if (!domain) {
    throw Error('Domain should be accessible')
  }
  const {
    domainUnfilledRecovery: {
      prebidSettingsWarnings: {
        allRecoveryAdUnitDisabled,
        multipleBasePathUnitsDetected,
        prebidStackDisabled,
        recoveryAdUnitNotAdded,
      },
      prebidSettingsWarnings,
    },
    id: domainId,
  } = domain

  const { baseAdUnitPath, prebidBehaviourType, unfilledRecoveryEnabled } = watch()

  const cannotUpdateDomainOrUnfilledRecovery: boolean =
    ability.cannot('update', 'Domain') || ability.cannot('update', 'Product-unfilledRecovery')
  const canReadbaseAdUnitPath: boolean = ability.can('read', 'DomainField-baseAdUnitPath')
  const cannotUpdatebaseAdUnitPath: boolean = ability.cannot('update', 'DomainField-baseAdUnitPath')

  const isUnfilledRecoveryNotConfigured: boolean = !isUnfilledRecoveryConfigured(baseAdUnitPath)
  const noPrebidStackAccess: boolean = ability.cannot('access', 'Product-prebidStack')

  const canReadPrebidSettingsWarning: boolean = ability.can(
    'read',
    'DomainFeature-prebidSettingsWarning',
  )
  const showPrebidSettingsWarning: boolean =
    prebidBehaviourType === 'runRecoveryPrebidAuction' &&
    canReadPrebidSettingsWarning &&
    (allRecoveryAdUnitDisabled ||
      prebidStackDisabled ||
      recoveryAdUnitNotAdded ||
      multipleBasePathUnitsDetected)

  const usePrebidBehavioursOptions = usePrebidBehavioursRadioGroup()

  useEffect(() => {
    if (isUnfilledRecoveryNotConfigured && unfilledRecoveryEnabled) {
      setValue('unfilledRecoveryEnabled', false)
    }
  }, [baseAdUnitPath])

  return (
    <>
      <Paper data-cy='unfilled-recovery-enable-section'>
        <SectionHeader
          subtitle={t('form.unfilledRecovery.enabled.description')}
          title={t('form.unfilledRecovery.enabled.title')}
          titleEndAdornment={
            <Switch
              control={control}
              disabled={cannotUpdateDomainOrUnfilledRecovery || isUnfilledRecoveryNotConfigured}
              name='unfilledRecoveryEnabled'
              {...(isUnfilledRecoveryNotConfigured && {
                tooltip: { content: t('form.unfilledRecovery.enabled.tooltip.notConfigured') },
                tooltipMode: 'wrap',
              })}
            />
          }
        />
      </Paper>

      <Paper data-cy='unfilled-recovery-basic-settings-section'>
        <SectionHeader
          subtitle={t('form.unfilledRecovery.basicSettings.subtitle')}
          title={t('form.unfilledRecovery.basicSettings.title')}
        />
        <GapBox />

        <BoxForm>
          {canReadbaseAdUnitPath && (
            <EmployeeBox>
              <TextField
                control={control}
                disabled={cannotUpdateDomainOrUnfilledRecovery || cannotUpdatebaseAdUnitPath}
                label={t('form.unfilledRecovery.basicSettings.baseAdUnitPath.label')}
                name='baseAdUnitPath'
              />
            </EmployeeBox>
          )}
        </BoxForm>
      </Paper>

      <Paper data-cy='unfilled-recovery-prebid-settings'>
        <SectionHeader
          subtitle={t('form.unfilledRecovery.prebidSettings.subtitle')}
          title={t('form.unfilledRecovery.prebidSettings.title')}
        />

        <GapBox />

        <RadioGroup
          control={control}
          disabled={cannotUpdateDomainOrUnfilledRecovery || noPrebidStackAccess}
          label={t('form.unfilledRecovery.prebidSettings.prebidBehaviourType.name')}
          name='prebidBehaviourType'
          options={usePrebidBehavioursOptions}
        />

        {showPrebidSettingsWarning && <PrebidSettingsAlert warnings={prebidSettingsWarnings} />}
      </Paper>

      <Box data-cy='unfilled-recovery-ad-units-section'>
        <Controller
          control={control}
          name='adUnitsAttributes'
          render={() => (
            <ProductAdUnitsTable
              domainId={domainId}
              product='unfilledRecovery'
            />
          )}
        />
      </Box>
    </>
  )
}
